import { useState, useMemo } from "react";
import { searchLocations } from "@/lib/philippine-locations";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check, MapPin, X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function LocationAutocomplete({ value, onValueChange, placeholder = "City, Province...", className = "", triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const results = useMemo(() => searchLocations(search, 25), [search]);

  const displayValue = value || "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="location-autocomplete-trigger" type="button"
          className={cn(
            "flex h-11 w-full items-center justify-between border-2 border-slate-200 bg-transparent px-3 py-2 text-sm font-medium transition-colors hover:border-slate-300 focus:border-court-orange focus:outline-none",
            triggerClassName
          )}>
          <span className="flex items-center gap-2 min-w-0">
            <MapPin className="w-4 h-4 text-court-orange shrink-0" />
            {displayValue ? (
              <span className="truncate">{displayValue}</span>
            ) : (
              <span className="text-muted-foreground truncate">{placeholder}</span>
            )}
          </span>
          {value ? (
            <span onClick={(e) => { e.stopPropagation(); onValueChange(""); setSearch(""); }}
              className="text-muted-foreground hover:text-foreground cursor-pointer p-0.5 shrink-0">
              <X className="w-3.5 h-3.5" />
            </span>
          ) : (
            <svg className="w-4 h-4 text-muted-foreground shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" data-testid="location-autocomplete-list">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type municipality, city or province..."
            className="h-10 text-sm" data-testid="location-autocomplete-input"
            value={search} onValueChange={setSearch} />
          <CommandList className="max-h-[280px]">
            <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">No location found</CommandEmpty>
            <CommandGroup>
              {results.map((loc, i) => (
                <CommandItem key={`${loc.value}-${i}`} value={loc.value}
                  data-testid={`location-option-${i}`}
                  onSelect={() => { onValueChange(loc.value); setOpen(false); setSearch(""); }}
                  className="flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm">
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{loc.location}</span>
                    <span className="text-muted-foreground">, {loc.province}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground/50 shrink-0">{loc.region}</span>
                  {value === loc.value && <Check className="w-4 h-4 text-court-orange shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
